import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Animated, {
  cancelAnimation,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSequence,
  withSpring,
  withTiming,
} from "react-native-reanimated";

import { Box } from "@whoosh/design-system/src/components/primitive";
import { IconProps } from "@whoosh/design-system/src/components/primitive/Icon";
import {
  AnimationDurations,
  AnimationEasings,
} from "@whoosh/design-system/src/theme/animations";

type IconComponent = React.ComponentType<IconProps>;

type AnimatedIconCyclerProps = {
  icons: ReadonlyArray<IconComponent>;
  iconProps?: Omit<IconProps, "color"> & { color?: IconProps["color"] };
  /** How long the icon stays fully visible before exiting (ms) */
  holdDurationMs?: number;
  /** Distance in px to travel vertically during enter/exit */
  travelPx?: number;
  /** Container width/height; defaults to iconProps width/height if provided */
  width?: number;
  height?: number;
};

/**
 *
 * This guy just cycles through the icons given to it and animates them floating into view and
 * then out. It's purely for our update loader.
 */
export const AnimatedIconCycler: React.FC<AnimatedIconCyclerProps> = ({
  icons,
  iconProps,
  holdDurationMs = 900,
  travelPx = 8,
  width,
  height,
}) => {
  const [index, setIndex] = useState<number>(0);

  const clampedIcons = useMemo(() => (icons.length > 0 ? icons : []), [icons]);
  const CurrentIcon =
    clampedIcons.length > 0 ? clampedIcons[index % clampedIcons.length] : null;

  const opacity = useSharedValue<number>(0);
  const translateY = useSharedValue<number>(travelPx);

  const isUnmounted = useRef<boolean>(false);

  const scheduleNext = useCallback(() => {
    if (isUnmounted.current) return;
    setIndex((prev) => (prev + 1) % (clampedIcons.length || 1));
    opacity.value = 0;
    translateY.value = travelPx;
  }, [clampedIcons.length, opacity, translateY, travelPx]);

  const runCycle = useCallback(() => {
    // Enter: fade in and move up with a subtle bounce
    opacity.value = withTiming(1, {
      duration: AnimationDurations.moderate01,
      easing: AnimationEasings.entrance,
    });
    translateY.value = withSpring(0, {
      damping: 18,
      stiffness: 160,
      mass: 0.7,
    });

    // After hold, exit: fade out and continue moving up
    opacity.value = withSequence(
      withTiming(1, { duration: AnimationDurations.fast01 }),
      withDelay(
        holdDurationMs,
        withTiming(
          0,
          {
            duration: AnimationDurations.moderate01,
            easing: AnimationEasings.exit,
          },
          (finished) => {
            if (finished) {
              runOnJS(scheduleNext)();
              // Slight delay before starting the next icon, for a smoother cadence
              opacity.value = withDelay(
                40,
                withTiming(0, { duration: 0 }, () => {
                  runOnJS(runCycle)();
                })
              );
            }
          }
        )
      )
    );

    translateY.value = withSequence(
      withTiming(0, { duration: AnimationDurations.fast01 }),
      withDelay(
        holdDurationMs,
        withTiming(-travelPx, {
          duration: AnimationDurations.moderate01,
          easing: AnimationEasings.exit,
        })
      )
    );
  }, [holdDurationMs, opacity, scheduleNext, translateY, travelPx]);

  useEffect(() => {
    isUnmounted.current = false;
    runCycle();
    return () => {
      isUnmounted.current = true;
      cancelAnimation(opacity);
      cancelAnimation(translateY);
    };
    // We intentionally exclude index here to let the finish callback schedule the next
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runCycle]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ translateY: translateY.value }],
  }));

  const containerWidth =
    width ?? (iconProps?.width as number | undefined) ?? undefined;
  const containerHeight =
    height ?? (iconProps?.height as number | undefined) ?? undefined;

  return (
    <Box
      width={containerWidth}
      height={containerHeight}
      justifyContent="center"
      alignItems="center"
    >
      <Animated.View style={animatedStyle} pointerEvents="none">
        {CurrentIcon ? <CurrentIcon {...iconProps} /> : null}
      </Animated.View>
    </Box>
  );
};

export default AnimatedIconCycler;
